import { Component, createRef } from "react";
import i18next from "../../locale/i18n";

interface GoalsOverlayState {
    globalStretchness: number;
    edgeSeams: number;
    stretchGoal: number | null;
    seamsGoal: number | null;
}

export class GoalsOverlay extends Component<{}, GoalsOverlayState> {
    callFunction: () => void;

    public state: GoalsOverlayState = {
        globalStretchness: 0,
        edgeSeams: 0,
        stretchGoal: null,
        seamsGoal: null
    };

    constructor(props: {}) {
        super(props);
        this.callFunction = () => {};
    }

    setEdgeSeams(numberEdgeSeams: number) {
        this.setState({ edgeSeams: numberEdgeSeams });
    }

    setGlobalStretchness(stretchness: number) {
        if (Number.isNaN(stretchness)) return;
        this.setState({ globalStretchness: parseFloat(stretchness.toFixed(2)) });
    }

    setGoal(goalStretch: number | null, goalSeams: number | null) {
        this.setState({ stretchGoal: goalStretch, seamsGoal: goalSeams });
    }

    setFunction(practiceFunction: () => void) {
        this.callFunction = practiceFunction;
    }

    getGoalColor(current: number, goal: number | null): string {
        if (goal === null) return 'inherit';
        return current <= goal ? '#22c55e' : '#ef4444';
    }

    // Calculate gauge: bar shows usage, red shows how much is over budget
    // Lower is better - less fill means more headroom
    getBudgetGauge(current: number, goal: number) {
        if (current <= goal) {
            // Under budget: green fill shows usage (0-100%)
            return {
                fillPercent: (current / goal) * 100,
                overflowPercent: 0
            };
        } else {
            // Over budget: use log scale to show progress even with extreme ratios
            // log10 compresses well: 10000x → 4, 100x → 2, 10x → 1
            const excessRatio = current / goal;
            const logRatio = Math.log10(excessRatio); // 10000→4, 100→2, 10→1, 1→0
            const greenPercent = 100 / (1 + logRatio);
            const redPercent = 100 - greenPercent;

            return {
                fillPercent: greenPercent,
                overflowPercent: redPercent
            };
        }
    }

    render() {
        const t = (key: string) => i18next.t(key);
        const { edgeSeams, globalStretchness, seamsGoal, stretchGoal } = this.state;

        const seamsMet = seamsGoal === null || edgeSeams <= seamsGoal;
        const stretchMet = stretchGoal === null || globalStretchness <= stretchGoal;
        const goalsMet = seamsMet && stretchMet;
        const buttonClass = goalsMet ? "btnOverlay" : "btnOverlay btnOverlayBlocked";

        return (
            <div className="goals-overlay">
                <div className="goals-header">{t('practice:goals')}</div>

                {/* Seams gauge */}
                <div className="goal-item">
                    <div className="goal-label">
                        <span>{t('practice:seamCount')}</span>
                        <span
                            className="goal-value"
                            style={{ color: this.getGoalColor(edgeSeams, seamsGoal) }}
                        >
                            {edgeSeams} / {seamsGoal ?? '—'}
                        </span>
                    </div>
                    {seamsGoal !== null && (() => {
                        const { fillPercent, overflowPercent } = this.getBudgetGauge(edgeSeams, seamsGoal);
                        return (
                            <div className="budget-gauge">
                                <div className="budget-gauge-fill" style={{ width: `${fillPercent}%` }} />
                                {overflowPercent > 0 && (
                                    <div className="budget-gauge-overflow" style={{ width: `${overflowPercent}%` }} />
                                )}
                            </div>
                        );
                    })()}
                </div>

                {/* Stretch gauge */}
                <div className="goal-item">
                    <div className="goal-label">
                        <span>{t('practice:stretch')}</span>
                        <span
                            className="goal-value"
                            style={{ color: this.getGoalColor(globalStretchness, stretchGoal) }}
                        >
                            {globalStretchness} / {stretchGoal ?? '—'}
                        </span>
                    </div>
                    {stretchGoal !== null && (() => {
                        const { fillPercent, overflowPercent } = this.getBudgetGauge(globalStretchness, stretchGoal);
                        return (
                            <div className="budget-gauge">
                                <div className="budget-gauge-fill striped" style={{ width: `${fillPercent}%` }} />
                                {overflowPercent > 0 && (
                                    <div className="budget-gauge-overflow" style={{ width: `${overflowPercent}%` }} />
                                )}
                            </div>
                        );
                    })()}
                </div>

                <button className={buttonClass} onClick={this.callFunction}>
                    {t('practice:next')}
                </button>

                {/* Accessibility: Live region for screen readers */}
                <div aria-live="polite" className="sr-only">
                    {goalsMet ? t('practice:goalsAchieved') : ""}
                </div>
            </div>
        );
    }
}
